import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { LOG_ENTRY_TYPE_LABELS, LogEntryType } from '../../../../../types/SystemLogging.types';
import {
  CHART_COLORS,
  monthNamesFull,
  monthNamesShort
} from './TrendsChart.constant';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TrendsChartProps {
  aiSystemId: string;
}

export function TrendsChart({ aiSystemId }: TrendsChartProps) {
  const { trends, trendsLoading } = useSelector(
    (state: RootState) => state.systemLogging
  );

  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: any[];
    yAxisMax?: number;
  }>({
    labels: [],
    datasets: []
  });
  const [types, setTypes] = useState<LogEntryType[]>([]);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!trends) return;

    try {
      const currentYear = new Date().getFullYear();
      const dataMap = new Map(trends.data.map((item) => [item.month, item]));

      // One series per log entry type seen anywhere in the period, sorted by
      // total volume so the busiest lines get the first (most distinct)
      // palette colors.
      const totalsByType = new Map<LogEntryType, number>();
      trends.data.forEach((item) => {
        Object.entries(item.byType).forEach(([type, count]) => {
          totalsByType.set(type as LogEntryType, (totalsByType.get(type as LogEntryType) ?? 0) + count);
        });
      });
      const sortedTypes = Array.from(totalsByType.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([type]) => type);

      const seriesByType = new Map<LogEntryType, (number | null)[]>();
      const pointRadiusByType = new Map<LogEntryType, number[]>();
      sortedTypes.forEach((type) => {
        seriesByType.set(type, []);
        pointRadiusByType.set(type, []);
      });

      for (let i = 0; i < 12; i++) {
        const fullMonthKey = `${monthNamesFull[i]} ${currentYear}`;
        const monthData = dataMap.get(fullMonthKey);

        sortedTypes.forEach((type) => {
          if (monthData) {
            seriesByType.get(type)!.push(monthData.byType[type] ?? 0);
            pointRadiusByType.get(type)!.push(4);
          } else {
            seriesByType.get(type)!.push(null);
            pointRadiusByType.get(type)!.push(0);
          }
        });
      }

      const allValues = sortedTypes
        .flatMap((type) => seriesByType.get(type)!)
        .filter((v): v is number => v !== null);
      const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
      const hasAnyData = allValues.some((value) => value > 0);
      setHasData(hasAnyData);
      setTypes(sortedTypes);

      // Round up to next even number (step is 2)
      const yAxisMax = hasAnyData ? Math.ceil((maxValue + 2) / 2) * 2 : 50;

      setChartData({
        labels: monthNamesShort,
        datasets: sortedTypes.map((type, index) => {
          const color = CHART_COLORS.palette[index % CHART_COLORS.palette.length];
          const pointRadius = pointRadiusByType.get(type)!;
          return {
            label: LOG_ENTRY_TYPE_LABELS[type],
            data: hasAnyData ? seriesByType.get(type) : [],
            borderColor: color,
            backgroundColor: color,
            tension: 0,
            pointRadius: hasAnyData ? pointRadius : 0,
            pointHoverRadius: hasAnyData ? 6 : 0,
            borderWidth: hasAnyData ? 2 : 0,
            spanGaps: false
          };
        }),
        yAxisMax
      });
    } catch (error) {
      console.error('Failed to process trends data:', error);
      setHasData(false);
    }
  }, [trends]);

  const data = chartData;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: hasData
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: chartData.yAxisMax,
        ticks: {
          stepSize: !hasData ? 10 : undefined,
          font: {
            family: 'Inter',
            size: 11
          }
        },
        grid: {
          display: true,
          color: CHART_COLORS.grid,
          drawBorder: false
        },
        border: {
          display: false
        }
      },
      x: {
        ticks: {
          font: {
            family: 'Inter',
            size: 11
          }
        },
        grid: {
          display: true,
          color: CHART_COLORS.grid,
          drawBorder: false
        },
        border: {
          display: false
        }
      }
    },
    ...(!hasData && { events: [] })
  };

  return (
    <div className="flex flex-col rounded-lg bg-white px-4 py-6 gap-3 ring-1 ring-[#DADADD] h-full">
      <span className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-[#767681] text-left">
        TRENDS
      </span>
      <div className="flex flex-col">
        <div className="h-[252px] relative">
          {trendsLoading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-gray-500">Loading trends data...</span>
            </div>
          ) : (
            <>
              <Line data={data} options={options} />
              {!hasData && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="font-inter font-medium text-sm leading-5 text-center text-[#111827] whitespace-nowrap">
                    No data available
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        {hasData ? (
          <div className="flex gap-3 justify-center flex-wrap">
            {types.map((type, index) => (
              <div key={type} className="flex items-center gap-1">
                <div
                  className="w-6 h-2 rounded-sm"
                  style={{
                    backgroundColor: CHART_COLORS.palette[index % CHART_COLORS.palette.length]
                  }}
                ></div>
                <span className="font-inter text-xs text-gray-700">
                  {LOG_ENTRY_TYPE_LABELS[type]}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-[16px]"></div>
        )}
      </div>
    </div>
  );
}
